import { Card, Container, Eyebrow } from '@tt/design-system';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CeremonialLoader } from '../../components/CeremonialLoader';
import { CertificateCard } from '../../components/CertificateCard';
import { Checkbox } from '../../components/form/Checkbox';
import { PaymentMethodSelector } from '../../components/PaymentMethods';
import { BLOCKCHAIN_MINT_MESSAGES } from '../../data/loaderMessages';
import { REGISTRATION_PRICE } from '../../lib/env';
import { useAuthStore } from '../../store/authStore';
import { useDraftStore } from '../../store/draftStore';
import { useRegistrationsStore } from '../../store/registrationsStore';
import type { PaymentConfirmPayload } from '../../types/payment';
import type { Registration } from '../../types/registration';
import {
  generateHashFake,
  generateRegistrationId,
  generateTxHashFake,
  generateVerificationCode,
} from '../../utils/generateId';
import { wait } from '../../utils/wait';

export default function Review() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const draft = useDraftStore((s) => s.draft);
  const setField = useDraftStore((s) => s.setField);
  const addRegistration = useRegistrationsStore((s) => s.add);

  const [isConfirming, setIsConfirming] = useState(false);

  // ID estável durante a sessão de revisão (não muda em re-renders)
  const previewId = useMemo(() => generateRegistrationId(), []);
  const previewCode = useMemo(() => generateVerificationCode(previewId), [previewId]);
  const previewCreatedAt = useMemo(() => new Date().toISOString(), []);

  if (!user) return null;

  async function handleConfirm(payload: PaymentConfirmPayload) {
    if (!user) return;
    setIsConfirming(true);
    // Tempo cerimonial — simula o mint on-chain pra o CeremonialLoader rodar as mensagens.
    // Em V1 isto vira o tempo real da transação na BSC.
    await wait(5000 + Math.random() * 1500);

    const registration: Registration = {
      id: previewId,
      displayName: draft.displayName.trim(),
      message: draft.message.trim(),
      photoUrl: draft.photoDataUrl ?? '',
      country: 'BR',
      createdAt: previewCreatedAt,
      verificationCode: previewCode,
      ownerEmail: user.email,
      verificationHashFake: generateHashFake(),
      txHashFake: generateTxHashFake(),
      paymentMethod: payload.method,
      paymentDetailsFake: payload.detailsFake,
    };

    addRegistration(registration);
    // Importante: NÃO chamar resetDraft() aqui. O <DraftGate> que protege esta rota
    // detectaria o draft vazio e redirecionaria pra /register antes do navigate
    // programático efetivar. O reset acontece no MyCollection ao detectar ?just=:id.
    navigate(`/minha-colecao?just=${registration.id}`, { replace: true });
  }

  return (
    <Container size="wide" className="py-12 md:py-16">
      <div className="mb-10 text-center">
        <Eyebrow tone="gold" className="mb-3">
          Revisão e pagamento
        </Eyebrow>
        <h1 className="font-display text-3xl text-parchment md:text-4xl">
          Um último olhar antes de eternizar.
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-neutral-100 md:text-base">
          Confira como o seu selo de presença vai ficar. Quando estiver pronta, confirme o
          pagamento — em modo demonstração, o botão simula a confirmação real.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.1fr_1fr] lg:gap-14">
        {/* Coluna do certificado */}
        <div>
          <Eyebrow tone="champagne" className="mb-4">
            Pré-visualização do certificado
          </Eyebrow>
          <CertificateCard
            registration={{
              id: previewId,
              displayName: draft.displayName.trim() || 'Sua presença',
              message: draft.message.trim() || 'Sua frase aparecerá aqui.',
              photoUrl: draft.photoDataUrl ?? '',
              verificationCode: previewCode,
              createdAt: previewCreatedAt,
            }}
            variant="preview"
            size="lg"
          />
        </div>

        {/* Coluna detalhes + LGPD + PIX */}
        <div className="flex flex-col gap-6">
          <Card tone="default" padding="lg">
            <Eyebrow tone="gold" className="mb-4">
              Detalhes do registro
            </Eyebrow>
            <dl className="flex flex-col gap-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="font-mono text-xs uppercase tracking-stamp text-champagne">Nome</dt>
                <dd className="text-right text-parchment">{draft.displayName}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="font-mono text-xs uppercase tracking-stamp text-champagne">
                  Frase
                </dt>
                <dd className="text-right italic text-parchment">“{draft.message}”</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="font-mono text-xs uppercase tracking-stamp text-champagne">País</dt>
                <dd className="text-right font-mono text-parchment">🇧🇷 Brasil</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="font-mono text-xs uppercase tracking-stamp text-champagne">
                  Identificador
                </dt>
                <dd className="text-right font-mono text-xs text-parchment">{previewId}</dd>
              </div>
            </dl>
          </Card>

          <Card tone="default" padding="lg">
            <Eyebrow tone="gold" className="mb-4">
              Consentimento
            </Eyebrow>
            <Checkbox
              checked={draft.consentLgpd}
              onChange={(e) => setField('consentLgpd', e.target.checked)}
              label={
                <span>
                  Estou de acordo que meu nome, frase e foto serão <strong>publicados</strong>{' '}
                  na galeria pública da taça e protegidos por tecnologia blockchain. Meu e-mail e
                  identidade de conta permanecem privados, conforme a{' '}
                  <a href="#" className="text-gold underline-offset-2 hover:underline">
                    política de privacidade
                  </a>{' '}
                  e a LGPD.
                </span>
              }
            />
          </Card>

          {draft.consentLgpd ? (
            <PaymentMethodSelector
              amount={REGISTRATION_PRICE}
              registrationId={previewId}
              onConfirm={handleConfirm}
              isConfirming={isConfirming}
            />
          ) : (
            <Card tone="subtle" padding="lg" className="text-center">
              <p className="font-mono text-xs uppercase tracking-stamp text-champagne/70">
                Aceite os termos para ver as opções de pagamento
              </p>
            </Card>
          )}
        </div>
      </div>

      <CeremonialLoader
        open={isConfirming}
        kicker="Registro permanente em andamento"
        title="Eternizando sua presença na rede."
        messages={BLOCKCHAIN_MINT_MESSAGES}
      />
    </Container>
  );
}
